// One icon system, two sources, one weight (DESIGN.md §9, FS-192: "own the nouns, keep the verbs").
// - Nouns are Bible Standard's own glyphs, copied verbatim from
//   BibleStandard/design/BibleStandardDesign/src/icons/owned/ (bs.*): 24-unit grid, 1.6 stroke,
//   round caps and joins, with .fill variants.
// - Verbs and controls come from Phosphor (regular weight, 1.5 at 24 px, round caps), the web's
//   counterpart to the SF Symbols the app uses for the same jobs; "fill" weight marks a selected tab,
//   as the app's tab bar does.
// Icons are decorative: the control around them carries the label.
import type { Icon as PhosphorIcon, IconWeight } from "@phosphor-icons/react";
import { ArrowCounterClockwise } from "@phosphor-icons/react/ArrowCounterClockwise";
import { ArrowSquareOut } from "@phosphor-icons/react/ArrowSquareOut";
import { ArrowUpRight } from "@phosphor-icons/react/ArrowUpRight";
import { Article } from "@phosphor-icons/react/Article";
import { CalendarBlank } from "@phosphor-icons/react/CalendarBlank";
import { Compass } from "@phosphor-icons/react/Compass";
import { Copy } from "@phosphor-icons/react/Copy";
import { SidebarSimple } from "@phosphor-icons/react/SidebarSimple";
import { ArrowsInSimple } from "@phosphor-icons/react/ArrowsInSimple";
import { ArrowsOutSimple } from "@phosphor-icons/react/ArrowsOutSimple";
import { BookmarkSimple } from "@phosphor-icons/react/BookmarkSimple";
import { Books } from "@phosphor-icons/react/Books";
import { CloudSlash } from "@phosphor-icons/react/CloudSlash";
import { Devices } from "@phosphor-icons/react/Devices";
import { EnvelopeSimple } from "@phosphor-icons/react/EnvelopeSimple";
import { FolderSimple } from "@phosphor-icons/react/FolderSimple";
import { List } from "@phosphor-icons/react/List";
import { Bell } from "@phosphor-icons/react/Bell";
import { BookOpenText } from "@phosphor-icons/react/BookOpenText";
import { CaretDown } from "@phosphor-icons/react/CaretDown";
import { CaretLeft } from "@phosphor-icons/react/CaretLeft";
import { CaretRight } from "@phosphor-icons/react/CaretRight";
import { ChatCircle } from "@phosphor-icons/react/ChatCircle";
import { Check } from "@phosphor-icons/react/Check";
import { DotsThree } from "@phosphor-icons/react/DotsThree";
import { DownloadSimple } from "@phosphor-icons/react/DownloadSimple";
import { Export } from "@phosphor-icons/react/Export";
import { Flag } from "@phosphor-icons/react/Flag";
import { GearSix } from "@phosphor-icons/react/GearSix";
import { HandsPraying } from "@phosphor-icons/react/HandsPraying";
import { Heart } from "@phosphor-icons/react/Heart";
import { ImageSquare } from "@phosphor-icons/react/ImageSquare";
import { Lightbulb } from "@phosphor-icons/react/Lightbulb";
import { LinkSimple } from "@phosphor-icons/react/LinkSimple";
import { LockSimple } from "@phosphor-icons/react/LockSimple";
import { Monitor } from "@phosphor-icons/react/Monitor";
import { Moon } from "@phosphor-icons/react/Moon";
import { NotePencil } from "@phosphor-icons/react/NotePencil";
import { Pause } from "@phosphor-icons/react/Pause";
import { PencilSimple } from "@phosphor-icons/react/PencilSimple";
import { Play } from "@phosphor-icons/react/Play";
import { Plus } from "@phosphor-icons/react/Plus";
import { TrayArrowDown } from "@phosphor-icons/react/TrayArrowDown";
import { TrayArrowUp } from "@phosphor-icons/react/TrayArrowUp";
import { Prohibit } from "@phosphor-icons/react/Prohibit";
import { Repeat } from "@phosphor-icons/react/Repeat";
import { SealCheck } from "@phosphor-icons/react/SealCheck";
import { ShieldCheck } from "@phosphor-icons/react/ShieldCheck";
import { SignOut } from "@phosphor-icons/react/SignOut";
import { Sparkle } from "@phosphor-icons/react/Sparkle";
import { Sun } from "@phosphor-icons/react/Sun";
import { Trash } from "@phosphor-icons/react/Trash";
import { User } from "@phosphor-icons/react/User";
import { UserMinus } from "@phosphor-icons/react/UserMinus";
import { UserPlus } from "@phosphor-icons/react/UserPlus";
import { Users } from "@phosphor-icons/react/Users";
import { X } from "@phosphor-icons/react/X";
import type { ReactNode } from "react";

/** Bible Standard's own glyphs (bs.*), verbatim. */
const owned = {
  home: <path d="M4 11l8-7 8 7v9h-5.5v-5.5h-5V20H4z" />,
  "home.fill": <path d="M4 11l8-7 8 7v9h-5.5v-5.5h-5V20H4z" fill="currentColor" />,
  ribbon: <path d="M8.5 3.5h7v17l-3.5-3.2-3.5 3.2z" />,
  "ribbon.fill": <path d="M8.5 3.5h7v17l-3.5-3.2-3.5 3.2z" fill="currentColor" />,
  note: (
    <>
      <path d="M6 4h9l3.5 3.5V20H6z" />
      <path d="M14.5 4v4H19" />
      <path d="M9 12.5h6" />
      <path d="M9 16h4" />
    </>
  ),
  bible: (
    <>
      <path d="M12 7c-2.4-1.5-5.4-1.9-8-1.1v12.7c2.6-.8 5.6-.4 8 1.1 2.4-1.5 5.4-1.9 8-1.1V5.9c-2.6-.8-5.6-.4-8 1.1z" />
      <path d="M12 7v12.7" />
      <path d="M14.6 3h2.4v5.3l-1.2-1-1.2 1z" fill="currentColor" />
    </>
  ),
  search: (
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.8-3.8" />
    </>
  ),
  highlight: (
    <>
      <path d="M5 19h14" />
      <path d="M8 15.5l8.5-8.5a2 2 0 0 1 3 3L11 18.5H8z" />
    </>
  ),
  quote: (
    <>
      <path d="M9.5 7.5C7 8.6 5.5 10.8 5.5 13.6c0 1.9 1.2 2.9 2.6 2.9s2.5-1 2.5-2.4-1-2.4-2.3-2.4c-.3 0-.6 0-.8.1.3-1.5 1.2-2.6 2.7-3.4z" />
      <path d="M18 7.5c-2.5 1.1-4 3.3-4 6.1 0 1.9 1.2 2.9 2.6 2.9s2.5-1 2.5-2.4-1-2.4-2.3-2.4c-.3 0-.6 0-.8.1.3-1.5 1.2-2.6 2.7-3.4z" />
    </>
  ),
} satisfies Record<string, ReactNode>;

/** Verbs and controls: [component, weight]. */
const verbs = {
  people: [Users, "regular"],
  "people.fill": [Users, "fill"],
  bell: [Bell, "regular"],
  "bell.fill": [Bell, "fill"],
  person: [User, "regular"],
  "person.fill": [User, "fill"],
  plus: [Plus, "regular"],
  // Keep in Pocket / Remove from Pocket (iOS tray.and.arrow.down / .up).
  "tray-in": [TrayArrowDown, "regular"],
  "tray-out": [TrayArrowUp, "regular"],
  compose: [NotePencil, "regular"],
  comment: [ChatCircle, "regular"],
  more: [DotsThree, "bold"],
  back: [CaretLeft, "regular"],
  forward: [CaretRight, "regular"],
  close: [X, "regular"],
  lock: [LockSimple, "regular"],
  share: [Export, "regular"],
  copy: [Copy, "regular"],
  image: [ImageSquare, "regular"],
  link: [LinkSimple, "regular"],
  edit: [PencilSimple, "regular"],
  trash: [Trash, "regular"],
  flag: [Flag, "regular"],
  block: [Prohibit, "regular"],
  "user-plus": [UserPlus, "regular"],
  "user-minus": [UserMinus, "regular"],
  open: [ArrowSquareOut, "regular"],
  read: [BookOpenText, "regular"],
  settings: [GearSix, "regular"],
  "sign-out": [SignOut, "regular"],
  download: [DownloadSimple, "regular"],
  sun: [Sun, "regular"],
  moon: [Moon, "regular"],
  device: [Monitor, "regular"],
  check: [Check, "bold"],
  undo: [ArrowCounterClockwise, "regular"],
  pause: [Pause, "regular"],
  play: [Play, "regular"],
  shield: [ShieldCheck, "regular"],
  spark: [Sparkle, "regular"],
  // The four responses, each its own glyph: regular until given, fill once it is (ReactionGlyph).
  amen: [SealCheck, "regular"],
  "amen.fill": [SealCheck, "fill"],
  praying: [HandsPraying, "regular"],
  "praying.fill": [HandsPraying, "fill"],
  love: [Heart, "regular"],
  "love.fill": [Heart, "fill"],
  insight: [Lightbulb, "regular"],
  "insight.fill": [Lightbulb, "fill"],
  // Today's verse is liked with the heart; Repost is the loop of arrows; the caret opens the responses.
  heart: [Heart, "regular"],
  "heart.fill": [Heart, "fill"],
  repost: [Repeat, "regular"],
  caret: [CaretDown, "bold"],
  // The reader and the library.
  bookmark: [BookmarkSimple, "regular"],
  "bookmark.fill": [BookmarkSimple, "fill"],
  library: [Books, "regular"],
  "cloud-off": [CloudSlash, "regular"],
  devices: [Devices, "regular"],
  email: [EnvelopeSimple, "regular"],
  folder: [FolderSimple, "regular"],
  contents: [List, "regular"],
  // The floating Bible: grow the panel, or bring it back to its size.
  expand: [ArrowsOutSimple, "regular"],
  /** The one action on a card that opens something: the ↗ disc. */
  go: [ArrowUpRight, "regular"],
  sidebar: [SidebarSimple, "regular"],
  calendar: [CalendarBlank, "regular"],
  "calendar.fill": [CalendarBlank, "fill"],
  article: [Article, "regular"],
  "article.fill": [Article, "fill"],
  compass: [Compass, "regular"],
  "compass.fill": [Compass, "fill"],
  "read.fill": [BookOpenText, "fill"],
  "library.fill": [Books, "fill"],
  collapse: [ArrowsInSimple, "regular"],
} satisfies Record<string, [PhosphorIcon, IconWeight]>;

export type IconName = keyof typeof owned | keyof typeof verbs;

export function Icon({ name, size = 22 }: { name: IconName; size?: number }) {
  if (name in verbs) {
    const [Glyph, weight] = verbs[name as keyof typeof verbs];
    return <Glyph size={size} weight={weight} aria-hidden="true" focusable="false" style={{ flex: "none" }} />;
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flex: "none" }}
    >
      {owned[name as keyof typeof owned]}
    </svg>
  );
}
